import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react"

import type { Annotation } from "@/types"
import { Avatar } from "@coval/ui/avatar"
import { Button } from "@coval/ui/button"
import { cn } from "@coval/ui/cn"
import { Popover, PopoverContent, PopoverTrigger } from "@coval/ui/popover"

import { ReviewerAvatarStack } from "@/components/ReviewerAvatarStack"
import { useResolveReviewer } from "@/hooks/useResolveReviewer"
import { getAvatarColor, getAvatarInk } from "@/utils/avatarIdentity"

const MAX_TRIGGER_AVATARS = 3
const HOVER_CLOSE_DELAY_MS = 150

interface OtherReviewerLabelsProps {
  annotations: Annotation[] | undefined
  /** The other reviewer's value, rendered ahead of their name in each row. */
  renderValue: (annotation: Annotation) => ReactNode
  /** Names what the labels are for, e.g. "criterion 2", in the button label. */
  subject?: string
  className?: string
}

/**
 * Shows who else has labeled this metric as a stack of their avatars, and
 * opens their values on hover (or click, for keyboard and touch). Renders
 * nothing when no one else has labeled it, rather than a button that does
 * nothing.
 */
export const OtherReviewerLabels = (props: OtherReviewerLabelsProps) =>
  props.annotations?.length ? (
    <OtherReviewerLabelsPopover {...props} annotations={props.annotations} />
  ) : null

const OtherReviewerLabelsPopover = ({
  annotations,
  renderValue,
  subject,
  className,
}: OtherReviewerLabelsProps & { annotations: Annotation[] }) => {
  const resolveReviewer = useResolveReviewer()

  const reviewers = useMemo(() => {
    const byEmail = new Map(
      annotations.map((annotation) => {
        const reviewer = resolveReviewer(annotation.assignee)
        return [reviewer.email.toLowerCase(), reviewer]
      }),
    )
    return Array.from(byEmail.values())
  }, [annotations, resolveReviewer])

  const names = reviewers.map((reviewer) => reviewer.name).join(", ")

  const [open, setOpen] = useState(false)
  // Hover-opening is a mouse-only preview: it leaves focus where the reviewer
  // is working, and a click must not toggle it shut. Touch and keyboard open
  // through the click and get the popover's normal focus handling.
  const openedByHover = useRef(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const cancelClose = () => clearTimeout(closeTimer.current)
  const openOnHover = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== "mouse") {
      return
    }
    cancelClose()
    if (!open) {
      openedByHover.current = true
    }
    setOpen(true)
  }
  // A short grace period lets the pointer cross the gap into the overlay
  // without it closing underneath.
  const closeOnLeave = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== "mouse" || !openedByHover.current) {
      return
    }
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS)
  }
  const handleOpenChange = (next: boolean) => {
    cancelClose()
    if (next) {
      openedByHover.current = false
    }
    setOpen(next)
  }
  useEffect(() => cancelClose, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-label={`View labels from ${names}${subject ? ` for ${subject}` : ""}`}
          // The overlay is the hover feedback; a fill behind the avatars
          // would only box them in again.
          className={cn("px-1 hover:bg-transparent", className)}
          onClick={(e) => {
            e.stopPropagation()
            // Leaving, Escape or clicking outside closes a hover-opened popover.
            if (open && openedByHover.current) {
              e.preventDefault()
            }
          }}
          onPointerEnter={openOnHover}
          onPointerLeave={closeOnLeave}
        >
          <ReviewerAvatarStack
            reviewers={reviewers}
            max={MAX_TRIGGER_AVATARS}
            size="sm"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="w-64 p-2"
        // Since a hover-open never moved focus in, closing has none to hand
        // back; returning it to the trigger would ring the button and its card.
        onOpenAutoFocus={(e) => {
          if (openedByHover.current) {
            e.preventDefault()
          }
        }}
        onCloseAutoFocus={(e) => {
          if (openedByHover.current) {
            e.preventDefault()
          }
        }}
        onPointerEnter={openOnHover}
        onPointerLeave={closeOnLeave}
      >
        <div className="max-h-48 overflow-y-auto">
          {annotations.map((annotation) => {
            const reviewer = resolveReviewer(annotation.assignee)
            return (
              <div
                key={annotation.id}
                className="flex items-center gap-2 px-1 py-1"
              >
                {renderValue(annotation)}
                <span className="flex min-w-0 items-center gap-1.5">
                  <Avatar
                    name={reviewer.name}
                    src={reviewer.imageUrl}
                    tint={getAvatarColor(reviewer.email.toLowerCase())}
                    style={{
                      color: getAvatarInk(reviewer.email.toLowerCase()),
                    }}
                    size="sm"
                    decorative
                  />
                  <span className="truncate text-xs text-coval-text-muted">
                    {reviewer.name}
                  </span>
                </span>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
